import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useGetPlayerByIdQuery } from "../../store/api/mlbApi";
import { useGetNbaPlayerByIdQuery } from "../../store/api/nbaApi";
import { useGetNflPlayerByIdQuery } from "../../store/api/nflApi";
import { galleryStepFromKey } from "../../utils/allTimeGreats";
import { getNflCareerTeamAbbrs } from "../../adapters/nflPlayerAdapter";
import { US_MAJOR_SCOPE } from "../../utils/nflLeagueScope";
import { getPlayerPath } from "../../utils/fanPage";
import PlayerBioSummary from "../PlayerBioSummary";
import PlayerHeadshot from "../PlayerHeadshot";

const GalleryArrow = ({ direction, disabled, onClick }) => {
  const isPrev = direction === "prev";
  return (
    <IconButton
      aria-label={isPrev ? "Previous player" : "Next player"}
      onClick={onClick}
      disabled={disabled}
      size="large"
      sx={{
        color: "text.primary",
        bgcolor: "action.hover",
        "&:hover": { bgcolor: "action.selected" },
        "&.Mui-disabled": { opacity: 0.3 },
      }}
    >
      {isPrev ? (
        <ChevronLeftIcon fontSize="large" />
      ) : (
        <ChevronRightIcon fontSize="large" />
      )}
    </IconButton>
  );
};

const GalleryBio = ({ playerId, dense, sport = "mlb", leagueScope = US_MAJOR_SCOPE }) => {
  const isNba = sport === "nba";
  const isNfl = sport === "nfl";
  const mlbQuery = useGetPlayerByIdQuery(playerId, {
    skip: !playerId || isNba || isNfl,
  });
  const nbaQuery = useGetNbaPlayerByIdQuery(playerId, {
    skip: !playerId || !isNba,
  });
  const nflQuery = useGetNflPlayerByIdQuery(playerId, {
    skip: !playerId || !isNfl,
  });
  const { data: player, isError } = isNfl ? nflQuery : isNba ? nbaQuery : mlbQuery;
  const matches = Boolean(playerId) && player?.playerId === playerId;

  const bio = matches ? player.bio : undefined;
  const nflBio =
    isNfl && bio
      ? { ...bio, teams: getNflCareerTeamAbbrs(player, leagueScope).join(", ") }
      : bio;

  return (
    <PlayerBioSummary
      bio={nflBio}
      sport={sport}
      loading={Boolean(playerId) && !matches && !isError}
      error={isError}
      dense={dense}
    />
  );
};

const GreatsGallery = ({
  open,
  item,
  index = 0,
  count = 0,
  statLabel,
  sport = "mlb",
  leagueScope = US_MAJOR_SCOPE,
  onClose,
  onStep,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [frame, setFrame] = useState({ item: null, index: 0, count: 0 });

  useEffect(() => {
    if (!item) return;
    setFrame({ item, index, count });
  }, [count, index, item]);

  const shown = item || frame.item;
  const shownIndex = item ? index : frame.index;
  const shownCount = item ? count : frame.count;

  const handleKeyDown = (event) => {
    const delta = galleryStepFromKey(event.key);
    if (delta == null) return;
    event.preventDefault();
    onStep?.(delta);
  };

  const playerId = shown?.playerId;
  const playerPagePath = getPlayerPath(playerId, sport);
  const canPrev = shownIndex > 0;
  const canNext = shownIndex < shownCount - 1;
  const photoSize = fullScreen ? 152 : 200;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      onKeyDown={handleKeyDown}
      aria-labelledby="greats-gallery-name"
      PaperProps={{
        sx: {
          bgcolor: "background.paper",
          backgroundImage: "none",
          overflow: "hidden",
        },
      }}
    >
      {shown ? (
        <Box sx={{ position: "relative" }}>
          <IconButton
            aria-label="Close gallery"
            onClick={onClose}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(240px, 0.9fr) 1.1fr",
              },
              minHeight: { md: 420 },
            }}
          >
            <Box
              sx={{
                px: { xs: 3, md: 4 },
                pt: { xs: 6, md: 5 },
                pb: { xs: 2, md: 5 },
                borderRight: { md: "1px solid" },
                borderBottom: { xs: "1px solid", md: 0 },
                borderColor: "divider",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: "0.16em", display: "block" }}
              >
                #{shown.rank}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-start" },
                  my: 2,
                }}
              >
                <PlayerHeadshot
                  playerId={playerId}
                  sport={sport}
                  src={shown.photoUrl}
                  size={photoSize}
                  showTooltip={false}
                  showLoading={false}
                  enabled={Boolean(playerId) && shown.photoUrl === undefined}
                  loading="eager"
                  fetchPriority="high"
                />
              </Box>
              <Typography
                id="greats-gallery-name"
                variant="h4"
                fontWeight={800}
                sx={{ lineHeight: 1.15 }}
              >
                {shown.playerName}
              </Typography>
              <Typography
                variant="h3"
                fontWeight={800}
                sx={{ mt: 1, fontVariantNumeric: "tabular-nums" }}
              >
                {shown.displayValue}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {statLabel}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2, letterSpacing: "0.06em" }}
              >
                {shownIndex + 1} of {shownCount}
              </Typography>
            </Box>

            <Box
              sx={{
                px: { xs: 3, md: 4 },
                py: { xs: 3, md: 5 },
                pr: { md: 6 },
                display: "flex",
                flexDirection: "column",
                gap: 3,
                height: "100%",
              }}
            >
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: "0.14em", display: "block", mb: 1.5 }}
                >
                  Bio
                </Typography>
                <GalleryBio playerId={playerId} dense={fullScreen} sport={sport} leagueScope={leagueScope} />
              </Box>

              <Box
                sx={{
                  mt: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {playerPagePath ? (
                  <Button
                    component={RouterLink}
                    to={playerPagePath}
                    variant="outlined"
                    endIcon={<OpenInNewIcon />}
                    sx={{ alignSelf: { xs: "stretch", md: "flex-start" } }}
                  >
                    Open player page
                  </Button>
                ) : null}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <GalleryArrow
                    direction="prev"
                    disabled={!canPrev}
                    onClick={() => onStep?.(-1)}
                  />
                  <GalleryArrow
                    direction="next"
                    disabled={!canNext}
                    onClick={() => onStep?.(1)}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Dialog>
  );
};

export default GreatsGallery;
